// ABOUTME: Per-integration status chips for one operator — enabled state, last sync, a whole-integration toggle, and click-to-filter selection
import { useOperatorIntegrations } from '../../hooks/useAdminQueries';
import { INTEGRATION_COLORS } from './integrationColors';
import './IntegrationStatusBar.css';

interface IntegrationStatusBarProps {
  operatorId: string;
  selected: string[];
  onSelect: (integration: string) => void;
  onToggle: (integration: string, enabled: boolean) => void;
  onToggleAll: (enabled: boolean) => void;
  onOpenNotifications: () => void;
}

function formatLastSync(lastSyncAt?: string): string {
  if (!lastSyncAt) return 'Never synced';
  const date = new Date(lastSyncAt);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Synced just now';
  if (diffMins < 60) return `Synced ${diffMins}m ago`;
  if (diffHours < 24) return `Synced ${diffHours}h ago`;
  return `Synced ${diffDays}d ago`;
}

export function IntegrationStatusBar({
  operatorId,
  selected,
  onSelect,
  onToggle,
  onToggleAll,
  onOpenNotifications,
}: IntegrationStatusBarProps) {
  const { data, isLoading } = useOperatorIntegrations(operatorId);

  if (isLoading) return null;

  const integrations = data?.integrations || [];

  // A selected vendor the status list cannot offer — a stale shared URL, or a
  // failed fetch — still filters the grid, so it must keep a chip the user can
  // click to clear the filter in place.
  const listed = new Set(integrations.map(({ integration }) => integration));
  const unlisted = selected.filter((vendor) => !listed.has(vendor));
  if (integrations.length === 0 && unlisted.length === 0) return null;

  // The operator reads on while any integration still syncs; the master
  // toggle disables or re-enables every integration in one action.
  const anyEnabled = integrations.some(({ enabled }) => enabled);

  return (
    <div className="integration-status-bar" data-testid="integration-status-bar">
      <span className="integration-status-label">INTEGRATIONS</span>
      {integrations.length > 0 && (
        <button
          className={`flow-toggle sm ${anyEnabled ? 'on' : 'off'}`}
          title={anyEnabled ? 'Disable all integrations' : 'Enable all integrations'}
          data-testid="integration-toggle-all"
          onClick={() => onToggleAll(anyEnabled)}
        >
          <span className="flow-toggle-track">
            <span className="flow-toggle-thumb" />
          </span>
        </button>
      )}
      {integrations.map(({ integration, enabled, lastSyncAt }) => (
        <div
          key={integration}
          className={`integration-status-chip ${enabled ? '' : 'disabled'} ${
            selected.includes(integration) ? 'selected' : ''
          }`}
          style={
            {
              '--chip-color': INTEGRATION_COLORS[integration] || '#3b82f6',
            } as React.CSSProperties
          }
          data-testid={`integration-status-${integration}`}
          onClick={() => onSelect(integration)}
        >
          <span className="integration-status-name">{integration}</span>
          <span className="integration-status-sync">
            {formatLastSync(lastSyncAt)}
          </span>
          <button
            className={`flow-toggle sm ${enabled ? 'on' : 'off'}`}
            data-testid={`integration-toggle-${integration}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(integration, enabled);
            }}
          >
            <span className="flow-toggle-track">
              <span className="flow-toggle-thumb" />
            </span>
          </button>
        </div>
      ))}
      {unlisted.map((integration) => (
        <div
          key={integration}
          className="integration-status-chip selected"
          style={
            {
              '--chip-color': INTEGRATION_COLORS[integration] || '#3b82f6',
            } as React.CSSProperties
          }
          data-testid={`integration-status-${integration}`}
          onClick={() => onSelect(integration)}
        >
          <span className="integration-status-name">{integration}</span>
        </div>
      ))}
      <button
        className="integration-notifications-btn"
        data-testid="open-notification-settings"
        title="Email notification settings"
        onClick={onOpenNotifications}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        Notifications
      </button>
    </div>
  );
}
